/**
 * Pure rules for CLI connection-health toasts: which issue the user's selected
 * personal CLI has, how often it may be announced, and what the single toast
 * action does. Bundled (managed) runtimes are the Denker updater's job.
 */
import { translate } from "@/i18n";
import { desktopConnectionHealthMessages } from "@/i18n/messages/desktop-connection-health";
import { CLI_REGISTRY } from "@/lib/cli-install-commands";
import type { DetectedOS } from "@/lib/detect-os";
import type { AvailableRuntime } from "@/stores/companion-store";
import type { SystemToast, SystemToastIcon } from "@/stores/notification-store";
import { runtimeTypeToCli } from "@/desktop/onboarding/post-demo-runtime-choice-utils";
import {
  isRuntimeType,
  personalRuntime,
} from "@/desktop/onboarding/post-demo-runtime-preference";
import type { RuntimeSetupMode } from "@/desktop/onboarding/runtime-setup-mode";
import type { RuntimeSetupRequest } from "@/desktop/onboarding/runtime-setup-status";

type RuntimeType = AvailableRuntime["type"];

export type RuntimeHealthIssue =
  | { kind: "missing"; runtimeType: RuntimeType }
  | {
      kind: "signed_out" | "update";
      runtimeType: RuntimeType;
      runtime: AvailableRuntime;
    };

/** A setup request plus the command copy mode hands to the user. */
export interface RuntimeActionRequest extends RuntimeSetupRequest {
  command: string;
}

const HOUR_MS = 3_600_000;
export const RUNTIME_HEALTH_TOAST_ID = "runtime-health";
export const RUNTIME_AUTH_TOAST_ID = "runtime-auth-required";
export const AUTH_REQUIRED_COOLDOWN_MS = 5 * 60_000;

const ACTION_KEY = {
  missing: "actionInstall",
  signed_out: "actionSignIn",
  update: "actionUpdate",
} as const satisfies Record<
  RuntimeHealthIssue["kind"],
  keyof (typeof desktopConnectionHealthMessages)["en"]
>;

const cliName = (runtimeType: RuntimeType) =>
  CLI_REGISTRY[runtimeTypeToCli(runtimeType)].name;

const cliIcon = (runtimeType: RuntimeType): SystemToastIcon => ({
  kind: "cli",
  cli: runtimeTypeToCli(runtimeType),
});

export function evaluateRuntimeHealth({
  runtimes,
  preferredRuntime,
  setupRequired,
}: {
  runtimes: readonly AvailableRuntime[];
  preferredRuntime: string | null;
  setupRequired: boolean;
}): RuntimeHealthIssue | null {
  if (setupRequired || !isRuntimeType(preferredRuntime)) return null;
  const runtime = personalRuntime(runtimes, preferredRuntime);
  if (!runtime) {
    const bundled = runtimes.some((r) => r.type === preferredRuntime);
    return bundled ? null : { kind: "missing", runtimeType: preferredRuntime };
  }
  if (runtime.logged_in === false) {
    return { kind: "signed_out", runtimeType: preferredRuntime, runtime };
  }
  if (runtime.update_available === true && runtime.update_command) {
    return { kind: "update", runtimeType: preferredRuntime, runtime };
  }
  return null;
}

export function runtimeHealthIssueKey(issue: RuntimeHealthIssue): string {
  const base = `runtime-health:${issue.kind}:${issue.runtimeType}`;
  return issue.kind === "update"
    ? `${base}:${issue.runtime.latest_version ?? ""}`
    : base;
}

export function runtimeHealthCooldownMs(issue: RuntimeHealthIssue): number {
  return issue.kind === "signed_out" ? 6 * HOUR_MS : 24 * HOUR_MS;
}

export function runtimeHealthToast(
  issue: RuntimeHealthIssue,
  mode: RuntimeSetupMode,
): Omit<SystemToast, "actionEvent"> {
  const name = cliName(issue.runtimeType);
  const actionLabel = translate(
    desktopConnectionHealthMessages,
    mode === "copy" ? "actionCopyCommand" : ACTION_KEY[issue.kind],
  );
  if (issue.kind === "missing") {
    return {
      id: RUNTIME_HEALTH_TOAST_ID,
      level: "warning",
      title: translate(desktopConnectionHealthMessages, "missingTitle", {
        name,
      }),
      description: translate(desktopConnectionHealthMessages, "missingBody"),
      actionLabel,
      persistent: true,
      icon: cliIcon(issue.runtimeType),
    };
  }
  if (issue.kind === "signed_out") {
    return {
      id: RUNTIME_HEALTH_TOAST_ID,
      level: "warning",
      title: translate(desktopConnectionHealthMessages, "signedOutTitle", {
        name,
      }),
      description: translate(desktopConnectionHealthMessages, "signedOutBody"),
      actionLabel,
      persistent: true,
      icon: cliIcon(issue.runtimeType),
    };
  }
  const latest = issue.runtime.latest_version;
  return {
    id: RUNTIME_HEALTH_TOAST_ID,
    level: "info",
    title: latest
      ? translate(desktopConnectionHealthMessages, "updateVersionTitle", {
          name,
          version: latest,
        })
      : translate(desktopConnectionHealthMessages, "updateTitle", { name }),
    description: translate(desktopConnectionHealthMessages, "updateBody"),
    actionLabel,
    icon: cliIcon(issue.runtimeType),
  };
}

export function runtimeAuthRequiredToast(
  runtimeType: RuntimeType,
  mode: RuntimeSetupMode,
): Omit<SystemToast, "actionEvent"> {
  return {
    id: RUNTIME_AUTH_TOAST_ID,
    level: "error",
    title: translate(desktopConnectionHealthMessages, "authExpiredTitle", {
      name: cliName(runtimeType),
    }),
    description: translate(desktopConnectionHealthMessages, "authExpiredBody"),
    actionLabel: translate(
      desktopConnectionHealthMessages,
      mode === "copy" ? "actionCopyCommand" : "actionSignIn",
    ),
    persistent: true,
    icon: cliIcon(runtimeType),
  };
}

export function runtimeActionRequest(
  issue: RuntimeHealthIssue,
  os: DetectedOS,
): RuntimeActionRequest | null {
  const choice = CLI_REGISTRY[runtimeTypeToCli(issue.runtimeType)];
  if (issue.kind === "missing") {
    return { choice, kind: "install", command: choice.commands[os] };
  }
  if (issue.kind === "signed_out") {
    return {
      choice,
      kind: "login",
      command: issue.runtime.login_command || choice.loginCommand,
      executablePath: issue.runtime.path,
    };
  }
  const command = issue.runtime.update_command;
  return command
    ? { choice, kind: "update", command, executablePath: issue.runtime.path }
    : null;
}
